/**
 * Consent banner for the marketing / web app.
 * One accept or reject choice covers every analytics and advertising
 * measurement tool on budgit.app (Google Analytics, the Reddit Pixel, the
 * LinkedIn Insight Tag, the X Pixel, the Meta Pixel).
 * The banner uses generic wording and names no tool; the named list lives
 * only in the Privacy Policy under How We Share Information (Jared,
 * 2026-10-18).
 */

import React, { useEffect, useState } from 'react';
import { Box, Button, Link, Typography, Paper } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  enableGoogleAnalytics,
  denyGoogleAnalytics,
  subscribeAnalyticsConsent,
} from '../services/analyticsConsent';
import { reopenCookieChoicesWithReloadWarning } from '../services/reloadOnConsentWithdrawal';
import { enableLinkedInInsightTag, flushLinkedInEventsAfterConsent } from '../services/linkedInInsightTag';
import { enableXPixel, flushXEventsAfterConsent } from '../services/xAdsPixel';
import { enableMetaPixel, flushMetaEventsAfterConsent } from '../services/metaPixel';

const PrivacyLink = ({ children }) => (
  <Link href="/privacy" underline="hover" color="inherit" sx={{ fontWeight: 600 }}>
    {children}
  </Link>
);

/**
 * @param {{ onDecision?: (choice: 'granted' | 'denied') => void }} props
 */
export default function AnalyticsConsentBanner({ onDecision }) {
  const [visible, setVisible] = useState(() => getAnalyticsConsent() == null);

  useEffect(() => {
    return subscribeAnalyticsConsent((choice) => {
      if (choice == null) {
        setVisible(true);
        return;
      }
      setVisible(false);
      if (onDecision) onDecision(choice);
    });
  }, [onDecision]);

  if (!visible) return null;

  const accept = () => {
    setAnalyticsConsent('granted');
    enableGoogleAnalytics();
    enableLinkedInInsightTag();
    flushLinkedInEventsAfterConsent();
    enableXPixel();
    flushXEventsAfterConsent();
    enableMetaPixel();
    flushMetaEventsAfterConsent();
    setVisible(false);
  };

  const reject = () => {
    setAnalyticsConsent('denied');
    denyGoogleAnalytics();
    setVisible(false);
  };

  return (
    <Box
      role="dialog"
      aria-label="Analytics"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.snackbar,
        p: { xs: 1.5, sm: 2 },
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          maxWidth: 720,
          mx: 'auto',
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          bgcolor: '#14143B',
          color: '#fff',
          border: '1px solid rgba(255,93,130,0.35)',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75, fontFamily: 'Quicksand, sans-serif' }}>
          Analytics
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.92, lineHeight: 1.5 }}>
          We use optional analytics and advertising measurement tools to understand how the
          site is used and whether ads lead to a download, signup, or subscription. These tools
          are not loaded until you accept. You can change your mind anytime with Cookie choices
          at the bottom of the page. See our <PrivacyLink>Privacy Policy</PrivacyLink>.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={reject}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 999,
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            onClick={accept}
            sx={{
              bgcolor: '#FF5D82',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 999,
              px: 2.5,
              '&:hover': { bgcolor: '#e84e72' },
            }}
          >
            Accept
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

/**
 * Small footer control for budgit.app. The homepage uses the Welcome footer.
 * Every other public route gets this fixed link, because those pages do not
 * share that footer.
 */
export function CookieChoicesLink() {
  const { pathname } = useLocation();
  if (pathname === '/') return null;

  return (
    <footer
      style={{
        position: 'fixed',
        left: 12,
        bottom: 8,
        zIndex: 1100,
      }}
    >
      <button
        type="button"
        onClick={reopenCookieChoicesWithReloadWarning}
        style={{
          fontFamily: 'Quicksand, sans-serif',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#14143B',
          background: 'rgba(255,255,255,0.92)',
          border: 0,
          borderRadius: 4,
          cursor: 'pointer',
          padding: '2px 6px',
        }}
      >
        Cookie choices
      </button>
    </footer>
  );
}
